export const PLAYBACK_RATES = [0.5, 0.6, 0.8, 1, 1.2, 1.5, 2];

const KOLLUS_IFRAME_SRC = 'v.kr.kollus.com';
const EXCLUDED_IFRAME_IDS = ['aodChild'];

const isKollusIframe = (element) => {
  if (element?.tagName !== 'IFRAME') return false;
  if (!element.src?.includes(KOLLUS_IFRAME_SRC)) return false;
  if (EXCLUDED_IFRAME_IDS.includes(element.id)) return false;
  return true;
};

export const applyKollusPlaybackRatesToIframe = (iframe, currentRate = 1) => {
  if (!iframe) return null;
  if (!isKollusIframe(iframe)) return null;
  if (!window.VgControllerClient || !iframe.contentWindow) return null;

  let controller = null;
  const timers = [];

  try {
    controller = new window.VgControllerClient({
      target_window: iframe.contentWindow,
    });

    const safeSetSpeed = (rate) => {
      try {
        if (controller && typeof controller.set_speed === 'function') {
          controller.set_speed(rate);
        }
      } catch (e) {
      }
    };

    const safeSetPlaybackRates = (rates) => {
      try {
        if (controller && typeof controller.set_playback_rates === 'function') {
          controller.set_playback_rates(JSON.stringify(rates));
        }
      } catch (e) {
      }
    };

    const applyPlaybackRates = () => {
      safeSetPlaybackRates(PLAYBACK_RATES);
      safeSetSpeed(currentRate);
    };

    if (typeof controller.on === 'function') {
      controller.on('loaded', () => {
        safeSetSpeed(currentRate);
      });

      controller.on('ready', () => {
        applyPlaybackRates();
      });

      controller.on('done', () => {
        const timer = window.setTimeout(() => {
          safeSetSpeed(currentRate);
        }, 500);
        timers.push(timer);
      });
    }

    iframe.addEventListener('load', applyPlaybackRates);

    return () => {
      iframe.removeEventListener('load', applyPlaybackRates);
      timers.forEach((timerId) => window.clearTimeout(timerId));

      if (controller) {
        try {
          if (typeof controller.off === 'function') {
            controller.off('loaded');
            controller.off('ready');
            controller.off('done');
          }
          if (typeof controller.destroy === 'function') {
            controller.destroy();
          }
        } catch (e) {
          // ignore cleanup error
        }
        controller = null;
      }
    };
  } catch (e) {
    console.log('[Kollus] initialize failed', e);
    return null;
  }
};

export const observeKollusIframes = () => {
  const cleanupMap = new WeakMap();

  const applyAllKollusIframes = () => {
    const currentIframes = new Set();

    document.querySelectorAll(`iframe[src*="${KOLLUS_IFRAME_SRC}"]`).forEach((iframe) => {
      currentIframes.add(iframe);
      if (cleanupMap.has(iframe)) return;
      if (EXCLUDED_IFRAME_IDS.includes(iframe.id)) return;

      const cleanup = applyKollusPlaybackRatesToIframe(iframe);
      if (cleanup) cleanupMap.set(iframe, cleanup);
    });
  };
  applyAllKollusIframes();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const iframes = node.matches?.(`iframe[src*="${KOLLUS_IFRAME_SRC}"]`)
          ? [node]
          : Array.from(node.querySelectorAll?.(`iframe[src*="${KOLLUS_IFRAME_SRC}"]`) || []);

        iframes.forEach((iframe) => {
          const cleanup = cleanupMap.get(iframe);
          if (cleanup) {
            cleanup();
            cleanupMap.delete(iframe);
          }
        });
      });
    });

    applyAllKollusIframes();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return () => {
    observer.disconnect();

    document.querySelectorAll(`iframe[src*="${KOLLUS_IFRAME_SRC}"]`).forEach((iframe) => {
      const cleanup = cleanupMap.get(iframe);
      if (cleanup) {
        cleanup();
        cleanupMap.delete(iframe);
      }
    });
  };
};
